import { RegistrationType, RegistrationStatus } from '@exam-center/graphql';
import { useParams } from '@exam-center/router';
import { Button, Divider, Flex, Form, FormDateRangeInput } from '@shared/components';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  RegistrationStatusSearchSelection,
  RegistrationTypeSearchSelection,
} from '../../components';
import { useExamEventScheduleSearchParams, useGetDefaultDateRange } from '../../hooks';

export const ExamEventScheduleFormSchema = z.object({
  registrationStatus: z.array(
    z.object({
      label: z.string(),
      value: z.nativeEnum(RegistrationStatus),
    }),
  ),
  registrationType: z.array(
    z.object({
      label: z.string(),
      value: z.nativeEnum(RegistrationType),
    }),
  ),
  dateRange: z.tuple([z.date(), z.date()]),
});

export type ExamEventScheduleFormType = z.infer<typeof ExamEventScheduleFormSchema>;

export default function ExamEventScheduleFilters() {
  const { month, year } = useParams('/:examCenterCode/exam-events/years/:year/months/:month');

  const defaultDateRange = useGetDefaultDateRange({
    month,
    year,
  });

  const [
    { startedAt, endedAt, registrationStatus, registrationType },
    { changeRegistrationStatus, changeRegistrationType, changePeriod },
  ] = useExamEventScheduleSearchParams({
    defaultValues: {
      startedAt: defaultDateRange[0],
      endedAt: defaultDateRange[1],
    },
  });

  const formContext = useForm<ExamEventScheduleFormType>({
    defaultValues: {
      registrationStatus: registrationStatus.map((status) => ({
        label: status,
        value: status as RegistrationStatus,
      })),
      registrationType: registrationType.map((type) => ({
        label: type,
        value: type as RegistrationType,
      })),
      dateRange: [startedAt ?? defaultDateRange[0], endedAt ?? defaultDateRange[1]],
    },
  });

  const handleResetFilter = () => {
    formContext.reset();
    handleSubmitFilter();
  };

  const handleSubmitFilter = () => {
    const { registrationStatus = [], registrationType = [], dateRange } = formContext.getValues();
    const mappedRegistrationStatus = registrationStatus.map<RegistrationStatus>(
      (status) => status.value,
    );
    changeRegistrationStatus(mappedRegistrationStatus);

    const mappedRegistrationType = registrationType.map<RegistrationType>((type) => type.value);
    changeRegistrationType(mappedRegistrationType);

    changePeriod(dateRange[0], dateRange[1]);
  };

  return (
    <FormProvider {...formContext}>
      <Form>
        <Flex
          sx={(theme) => ({
            flexDirection: 'column',
            gap: theme.spacing(4),
            p: theme.spacing(4),
            border: `1px solid ${theme.palette.grey[300]}`,
            borderRadius: theme.shape.rounded(2),
          })}
        >
          <Flex
            sx={(theme) => ({
              gap: theme.spacing(4),
            })}
          >
            <RegistrationStatusSearchSelection
              name='registrationStatus'
              value={(formContext.watch('registrationStatus') ?? []).map((item) => item.value)}
            />

            <RegistrationTypeSearchSelection
              name='registrationType'
              value={(formContext.watch('registrationType') ?? []).map((item) => item.value)}
            />
            <FormDateRangeInput
              name='dateRange'
              label='วันที่'
              minDate={defaultDateRange[0]}
              maxDate={defaultDateRange[1]}
              defaultValue={[startedAt, endedAt]}
              onClear={() => {
                formContext.setValue('dateRange', defaultDateRange);
              }}
            />
          </Flex>
          <Divider />
          <Flex
            sx={(theme) => ({
              gap: theme.spacing(2),
              justifyContent: 'flex-end',
            })}
          >
            <Button
              variant='outlined'
              color='secondary'
              sx={{ width: '100px' }}
              onClick={handleResetFilter}
            >
              Reset
            </Button>
            <Button variant='contained' sx={{ width: '100px' }} onClick={handleSubmitFilter}>
              Apply
            </Button>
          </Flex>
        </Flex>
      </Form>
    </FormProvider>
  );
}
